import {
  type CartLineInput,
  type AttributeInput,
  type Cart,
} from '@shopify/hydrogen-react/storefront-api-types';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

import { cartAtom } from '../atoms/cartAtom';
import { client } from '../client';
import { cartLinesAdd, cartLinesRemove } from '../mutations/cart';

export const useCart = ({
  onChangeStart,
  onChangeFinish,
}: {
  onChangeStart?: () => void;
  onChangeFinish?: () => void;
}) => {
  const [cart, setCart] = useAtom(cartAtom);

  const isProductVariantSelected = useCallback(
    (productVariantId: string) => {
      return cart?.lines.nodes.some(
        (line) => line.merchandise.id === productVariantId,
      );
    },
    [cart],
  );

  const getLineItem = useCallback(
    (productVariantId: string) => {
      return cart?.lines.nodes.find(
        (line) => line.merchandise.id === productVariantId,
      );
    },
    [cart],
  );

  const removeItem = useCallback(
    async (productVariantId: string) => {
      if (!cart) {
        throw new Error('Cannot remove item: cart is not defined');
      }

      const lineItem = cart?.lines.nodes.find(
        (line) => line.merchandise.id === productVariantId,
      );
      if (!lineItem) {
        throw new Error('Cannot remove item: item not found in cart');
      }

      onChangeStart?.();

      const { data, errors } = await client.request(cartLinesRemove, {
        variables: {
          cartId: cart.id,
          lineIds: [lineItem.id],
        },
      });

      onChangeFinish?.();

      if (errors?.graphQLErrors) {
        throw new Error(errors.graphQLErrors[0].message);
      }

      setCart(data.cartLinesRemove.cart as Cart);
    },
    [cart],
  );

  const addItem = useCallback(
    async ({
      productVariantId,
      sellingPlanId,
      attributes,
    }: {
      productVariantId: string;
      sellingPlanId?: string;
      attributes: AttributeInput[];
    }) => {
      if (!cart) {
        throw new Error('Cannot remove item: cart is not defined');
      }

      const lineItem = {
        merchandiseId: productVariantId,
        sellingPlanId,
        quantity: 1,
        attributes,
      } satisfies CartLineInput;

      onChangeStart?.();

      const { data, errors } = await client.request(cartLinesAdd, {
        variables: {
          cartId: cart.id,
          lines: [lineItem],
        },
      });

      onChangeFinish?.();

      if (errors?.graphQLErrors) {
        throw new Error(errors.graphQLErrors[0].message);
      }

      setCart(data.cartLinesAdd.cart as Cart);
    },
    [cart],
  );

  return {
    cart,
    isProductVariantSelected,
    removeItem,
    addItem,
    getLineItem,
  };
};
